import { useState } from "react";
import { usePage, Head } from "@inertiajs/react";
import type { SharedPageProps } from "~/layouts/types";
import {
  Check,
  X,
  Rocket,
  Zap,
  Star,
  Sparkles,
  Settings,
  BarChart3,
  Plug,
  UserPlus,
  CreditCard,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";

interface AvailablePlan {
  id: string;
  tier_name: string;
  display_name: string;
  interval: string;
  amount_cents: number;
  currency: string;
  credits: number;
  features: string[];
  trial_period_days: number;
}

interface PricingProps {
  [key: string]: unknown;
  available_plans: AvailablePlan[];
}

interface TierGroup {
  tierName: string;
  displayName: string;
  monthly: AvailablePlan | null;
  annual: AvailablePlan | null;
}

// Presentation metadata mirrored from the marketing site so the in-app pricing
// page matches it (landing_page/src/data/pricing.ts). Prices and trial length
// still come from the live DB plans; this only supplies the per-tier icon,
// tagline, and included/excluded capability matrix the cards render.
interface TierMeta {
  icon: LucideIcon;
  tagline: string;
  highlighted: boolean;
  badge?: string;
  featureList: { text: string; included: boolean }[];
}

const TIER_META: Record<string, TierMeta> = {
  starter: {
    icon: Rocket,
    tagline: "Launch and test your first campaign",
    highlighted: false,
    featureList: [
      { text: "Up to 2 campaigns/month", included: true },
      { text: "Custom landing pages", included: true },
      { text: "Lead capture & delivery", included: true },
      { text: "Performance analytics", included: true },
      { text: "Custom domains", included: false },
      { text: "24/7 priority support", included: false },
    ],
  },
  growth: {
    icon: Zap,
    tagline: "Run more campaigns, scale what works",
    highlighted: true,
    badge: "Most Popular",
    featureList: [
      { text: "Up to 10 campaigns/month", included: true },
      { text: "Custom landing pages", included: true },
      { text: "Lead capture & delivery", included: true },
      { text: "Performance analytics", included: true },
      { text: "Custom domains", included: true },
      { text: "24/7 priority support", included: false },
    ],
  },
  pro: {
    icon: Star,
    tagline: "Maximum power for busy teams",
    highlighted: false,
    featureList: [
      { text: "Up to 40 campaigns/month", included: true },
      { text: "Custom landing pages", included: true },
      { text: "Lead capture & delivery", included: true },
      { text: "Performance analytics", included: true },
      { text: "Custom domains", included: true },
      { text: "24/7 priority support", included: true },
    ],
  },
};

const BASE_FEATURE_GROUPS: { category: string; items: { text: string; icon: LucideIcon }[] }[] = [
  {
    category: "Pages & ads",
    items: [
      { text: "Landing pages and Google Ads, built in minutes", icon: Rocket },
      { text: "Cited by AI answers in ChatGPT, Claude, Gemini, Perplexity", icon: Sparkles },
    ],
  },
  {
    category: "Reporting & optimizations",
    items: [
      { text: "Tracking and analytics set up for you, no code or consultants", icon: Settings },
      { text: "Analytics intelligence: spend, clicks, and leads in one view", icon: BarChart3 },
    ],
  },
  {
    category: "Integrations & team",
    items: [
      { text: "Connect to HubSpot, Monday, Mailchimp, and 5,000+ other apps", icon: Plug },
      { text: "Unlimited users: invite your whole team at no extra cost", icon: UserPlus },
    ],
  },
];

function groupPlansByTier(plans: AvailablePlan[]): TierGroup[] {
  const tiers = new Map<string, TierGroup>();

  for (const plan of plans) {
    if (!tiers.has(plan.tier_name)) {
      tiers.set(plan.tier_name, {
        tierName: plan.tier_name,
        displayName: plan.display_name,
        monthly: null,
        annual: null,
      });
    }
    const group = tiers.get(plan.tier_name)!;
    if (plan.interval === "month") {
      group.monthly = plan;
    } else {
      group.annual = plan;
    }
  }

  return Array.from(tiers.values());
}

function monthlyPrice(plan: AvailablePlan): string {
  const cents = plan.interval === "year" ? plan.amount_cents / 12 : plan.amount_cents;
  return Math.round(cents / 100).toString();
}

// Shown above the plans when the user's last charge failed (trial→paid decline
// or a renewal decline). For these users updating the card — not picking a new
// plan — is the real action, so this is the page's primary CTA.
function PaymentRecoveryBanner({
  recovery,
}: {
  recovery: NonNullable<SharedPageProps["payment_recovery"]>;
}) {
  const planLabel = recovery.plan_display_name ? `${recovery.plan_display_name} plan` : "subscription";

  return (
    <div className="mb-8 rounded-xl border border-[#df6d4a]/30 bg-white shadow-sm overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-[#df6d4a] to-[#e2915f]" />
      <div className="p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex items-start gap-4 flex-grow">
          <div className="w-11 h-11 rounded-full bg-[#df6d4a]/10 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-[#df6d4a]" strokeWidth={2.25} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#12183d]">
              Your payment didn't go through
            </h2>
            <p className="text-[#6f6e6b] text-sm mt-1 max-w-xl">
              {recovery.decline_message ||
                "Your card on file was declined, so your subscription is paused. Update your payment method to restore access."}
            </p>
            <p className="text-[#8b8986] text-xs mt-2">
              No need to pick a new plan — your {planLabel} is still here, it just needs a working card.
            </p>
          </div>
        </div>
        <a
          href={recovery.update_payment_method_path}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap py-2.5 px-6 rounded-lg font-medium bg-[#df6d4a] text-white hover:bg-[#df6d4a]/90 transition-all flex-shrink-0"
        >
          <CreditCard className="w-4 h-4" strokeWidth={2.25} />
          Update payment method
        </a>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { available_plans } = usePage<PricingProps>().props;
  const paymentRecovery = (usePage().props as SharedPageProps).payment_recovery;
  const [isAnnual, setIsAnnual] = useState(true);

  // `has_ever_subscribed` rides on the shared credits prop (inertia_share) — the
  // same flag the out-of-credits modal uses. A never-subscribed account is the
  // only one that actually gets a trial at checkout, so gate the free-trial
  // framing on it rather than duplicating the lookup here.
  const credits = (usePage().props as SharedPageProps).credits;
  const neverSubscribed = credits?.has_ever_subscribed === false;

  const tierGroups = groupPlansByTier(available_plans);

  // Longest trial offered across plans. All paid plans currently share the same
  // length, but Math.max keeps it correct if they ever drift.
  const trialDays = Math.max(0, ...available_plans.map((p) => p.trial_period_days ?? 0));
  const showTrial = neverSubscribed && trialDays > 0;

  function getActivePlan(group: TierGroup): AvailablePlan | null {
    return isAnnual ? (group.annual ?? group.monthly) : (group.monthly ?? group.annual);
  }

  function handleSelectPlan(plan: AvailablePlan) {
    window.location.href = `/subscriptions/new?plan=${plan.id}`;
  }

  return (
    <main className="min-h-full bg-[#fafaf9] py-16 px-4 md:px-6">
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Serif:ital,wght@1,500;1,600&display=swap"
          rel="stylesheet"
        />
      </Head>

      <div className="mx-auto w-full max-w-5xl">
        {paymentRecovery && <PaymentRecoveryBanner recovery={paymentRecovery} />}

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-[#12183d] leading-tight">
            Agency results.{" "}
            <span className="font-['IBM_Plex_Serif',serif] italic bg-gradient-to-r from-[#3748b8] to-[#df6d4a] bg-clip-text text-transparent pr-1">
              Self-serve pricing.
            </span>
          </h1>
          <p className="text-[#6f6e6b] max-w-2xl mx-auto text-lg mt-4">
            One new customer pays for a year of Launch10. Most pay it back in week one.
          </p>
        </div>

        {/* Card container */}
        <div className="bg-white border border-[#e2e1e0] rounded-xl p-6 md:p-8 shadow-sm">
          {/* Trial banner */}
          {showTrial && (
            <div className="flex items-center justify-center gap-2 mb-6 py-2.5 px-4 rounded-lg bg-gradient-to-r from-[#3748b8]/10 via-[#df6d4a]/10 to-[#3748b8]/10 border border-[#3748b8]/20">
              <Sparkles className="w-4 h-4 text-[#df6d4a] flex-shrink-0" strokeWidth={2.25} />
              <p className="text-sm text-[#12183d] text-center">
                <span className="font-semibold">Start free today — no payment to begin.</span>{" "}
                <span className="text-[#6f6e6b]">
                  {trialDays}-day trial. Cancel anytime and you won't be charged.
                </span>
              </p>
            </div>
          )}

          {/* Toggle */}
          <div className="flex items-center justify-center mb-8">
            <div className="inline-flex items-center bg-[#ededec] border border-[#e2e1e0] rounded-lg p-1">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-4 py-2 text-sm rounded-md transition-all ${
                  !isAnnual ? "bg-white shadow-sm font-medium text-[#12183d]" : "text-[#8b8986]"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-4 py-2 text-sm rounded-md transition-all ${
                  isAnnual ? "bg-white shadow-sm font-medium text-[#12183d]" : "text-[#8b8986]"
                }`}
              >
                Annual
              </button>
            </div>
            <span className="ml-3 bg-[#eaf5f3] text-[#5f7e78] text-xs font-medium px-3 py-1 rounded-full">
              Save 25%
            </span>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {tierGroups.map((group) => {
              const plan = getActivePlan(group);
              if (!plan) return null;

              const meta = TIER_META[group.tierName];
              const highlighted = meta?.highlighted ?? false;
              const TierIcon = meta?.icon ?? Rocket;
              const featureList =
                meta?.featureList ?? plan.features.map((text) => ({ text, included: true }));

              return (
                <div
                  key={group.tierName}
                  className={`group relative bg-white rounded-xl flex flex-col transition-all duration-300 ${
                    highlighted
                      ? "border-2 border-[#3748b8] shadow-xl md:scale-105 z-10 hover:shadow-2xl"
                      : "border border-[#e2e1e0] shadow-sm hover:border-[#3748b8]/30 hover:shadow-lg hover:-translate-y-1"
                  }`}
                >
                  {meta?.badge && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-[#3748b8] text-white text-xs font-medium px-3 py-1 rounded-full">
                        {meta.badge}
                      </span>
                    </div>
                  )}

                  <div className="p-6 flex flex-col flex-grow">
                    {/* Plan name */}
                    <div className="flex items-center gap-2 h-8">
                      <div className="w-7 h-7 rounded-full bg-[#eaf5f3] flex items-center justify-center">
                        <TierIcon className="w-5 h-5 text-[#5f7e78]" />
                      </div>
                      <h3 className="font-semibold text-xl text-[#12183d]">{group.displayName}</h3>
                    </div>

                    {/* Tagline */}
                    {meta?.tagline && (
                      <p className="text-sm text-[#8b8986] font-normal h-10 mt-1">{meta.tagline}</p>
                    )}

                    {/* Price */}
                    <div className="mt-2">
                      <span className="text-4xl font-bold text-[#12183d]">${monthlyPrice(plan)}</span>
                      <span className="text-base font-normal text-[#8b8986]">/mo</span>
                      <p className="text-xs text-[#8b8986] mt-0.5">
                        {isAnnual && "billed annually"}
                        {isAnnual && showTrial && " · "}
                        {showTrial && `${plan.trial_period_days}-day free trial`}
                        {!isAnnual && !showTrial && "billed monthly"}
                      </p>
                    </div>

                    {/* Features */}
                    <ul className="mt-4 space-y-2.5">
                      {featureList.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2.5">
                          {feature.included ? (
                            <Check className="w-4 h-4 text-[#5f7e78] flex-shrink-0" />
                          ) : (
                            <X className="w-4 h-4 text-[#d3d2d0] flex-shrink-0" />
                          )}
                          <span
                            className={`text-sm ${
                              feature.included ? "text-[#6f6e6b]" : "text-[#c4c3c0]"
                            }`}
                          >
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Button */}
                    <button
                      onClick={() => handleSelectPlan(plan)}
                      className={`block w-full py-2.5 px-6 rounded-lg text-center font-medium transition-all mt-5 ${
                        highlighted
                          ? "bg-[#3748b8] text-white hover:bg-[#3748b8]/90"
                          : "bg-[#12183d] text-white hover:bg-[#12183d]/90"
                      }`}
                    >
                      {showTrial ? "Start for free" : "Choose plan"}
                    </button>
                    {showTrial && (
                      <p className="mt-3 text-xs text-[#8b8986] text-center">
                        {plan.trial_period_days}-day free trial · Cancel anytime
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Every plan also includes */}
          <div className="mt-10 pt-8 border-t border-[#e2e1e0]">
            <h3 className="text-base font-semibold text-[#12183d] text-center mb-8">
              Every plan also includes
            </h3>
            <div className="space-y-8">
              {BASE_FEATURE_GROUPS.map((group) => (
                <div key={group.category}>
                  <p className="text-xs font-bold tracking-[0.18em] text-[#df6d4a] uppercase mb-4">
                    {group.category}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                    {group.items.map((item, index) => {
                      const ItemIcon = item.icon;
                      return (
                        <div key={index} className="flex items-start gap-3">
                          <ItemIcon className="w-5 h-5 text-[#5f7e78] flex-shrink-0 mt-0.5" />
                          <span className="text-[#6f6e6b] text-sm leading-relaxed">{item.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
